import React, { FC, ReactNode, useCallback, useMemo, useState } from "react";
import classNames, { Argument } from "classnames/dedupe";
import { SearchInput } from "../../components/Navigational/Search";
import {
  SvgIcon,
  TThemeVariant,
  DropdownOption,
  DropdownAlignment,
  DropdownToggleStrategy,
} from "../../components";
import { MyLocationIcon } from "../../icons";
import DropdownList from "../../components/Utility/Dropdown/DropdownList";
import Dropdown from "../../components/Utility/Dropdown/Dropdown";
import ScrollContainer from "../../components/Utility/ScrollContainer/ScrollContainer";
import useTheme from "../../components/Utility/ThemeProvider/useTheme";

export interface IMapSearchInput {
  classes?: {
    dropdown?: {
      button?: Argument;
    };
    button?: Argument;
  };
  placeHolder: string;
  value: string;
  theme?: TThemeVariant;
  labelKey?: string;
  valueKey?: string;
  searchResults?: Array<unknown>;
  minSearchLength?: number;
  noResultsPlaceholder?: ReactNode;
  onChange(newValue: string);
  onSelect(selectedOption: unknown);
}

const MapSearchInput: FC<IMapSearchInput> = ({
  classes,
  placeHolder,
  theme: propsTheme = "dark",
  value,
  searchResults,
  valueKey = "id",
  labelKey = "label",
  noResultsPlaceholder,
  minSearchLength = 3,
  onChange,
  onSelect,
}) => {
  const [inputFocused, setInputFocused] = useState(false);
  const theme = useTheme(propsTheme);

  const handleSelect = useCallback(
    (v) => {
      onSelect?.(v);
    },
    [onSelect],
  );
  const onFocus = useCallback(() => {
    setInputFocused(true);
  }, []);
  const onBlur = useCallback(() => {
    setInputFocused(false);
  }, []);

  const buttonContent = useMemo(() => {
    return (
      <form
        className={classNames("search-container flex-grow-1", classes?.button)}
      >
        <SearchInput
          searchTerm={value}
          placeholderLabel={placeHolder}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
        />
        <button type="button" aria-label="Your location">
          <SvgIcon
            icon={MyLocationIcon}
            classes={{ root: "icon-my-location" }}
          />
        </button>
      </form>
    );
  }, [classes, onChange, placeHolder, value]);

  const searchMenu = useMemo(() => {
    const hasMinCharsValue: boolean = value?.length >= minSearchLength;

    if (!hasMinCharsValue) {
      return null;
    }

    return (
      <div className="dropdown-menu-body">
        {searchResults?.length ? (
          <ScrollContainer height="auto">
            {searchResults.map((res) => (
              <DropdownOption
                key={res[valueKey]}
                item={res}
                valueKey={valueKey as keyof typeof res}
                labelKey={labelKey as keyof typeof res}
                onSelect={handleSelect}
              />
            ))}
          </ScrollContainer>
        ) : (
          <div>{noResultsPlaceholder}</div>
        )}
      </div>
    );
  }, [value, minSearchLength, searchResults]);

  const getListContent = useCallback(
    () => (
      <div
        className={classNames(
          "dropdown-menu show position-fixed position-md-relative w-auto left-0 top-0 mt-3",
          {
            "dropdown-menu--dark": theme === "dark",
          },
        )}
      >
        <div
          className={classNames("dropdown-menu__content-wrapper", {
            "dropdown-menu__content-wrapper--dark": theme === "dark",
          })}
        >
          {searchMenu}
        </div>
      </div>
    ),
    [value, minSearchLength, searchResults, searchMenu],
  );

  return (
    <Dropdown
      theme="dark"
      buttonTheme="light"
      classes={classes?.dropdown}
      opened={inputFocused && value && value.length >= minSearchLength}
      dropdownButton={buttonContent}
      toggleStrategy={DropdownToggleStrategy.NONE}
      alignment={DropdownAlignment.FIT}
    >
      <DropdownList>{getListContent}</DropdownList>
    </Dropdown>
  );
};

export default MapSearchInput;
